import { useState } from "react";
import { createPortal } from "react-dom";
import { T, UI, FONT } from "../constants/tokens";
import { TYPE } from "../constants/typography";
import { supabase } from "../lib/supabase";
import { requestAccountDeletion, deletionSchedule, formatDate, GRACE_DAYS } from "../lib/accountService";
import { Card } from "./Card";
import Icon from "./Icon";
import LTR from "./LTR";
import { modalScrim, modalPanel } from "./Modal";

// ─── Account deletion — the request half ─────────────────────────────────────
// Sits directly after the export card in Settings, so the order on screen is
// the order the user needs: take your data out, then close the account.
//
// Colour: UI.caution, not red. VISION reserves red for a value going down and
// says so about warnings in particular, so the weight here comes from the
// wording and from the two-step confirmation rather than from a danger colour.
//
// Every sentence below is a statement of what happens and when. There is no
// "are you sure", no reason-for-leaving question, and nothing that argues for
// staying — the user already decided, and this screen's job is to make sure
// what they decided is what actually happens.

// What the confirmation lists as lost. Kept as data so the wording is in one
// place and matches what the cascade actually removes.
const WHAT_IS_DELETED = [
  "התיק וההחזקות שבו",
  "התראות המחיר",
  "חשבון הסימולציה והעסקאות בו",
  "תשובות שאלון פרופיל המשקיע",
  "תמונת הפרופיל",
];

function ConfirmModal({ onCancel, onConfirm, busy, error }) {
  const [understood, setUnderstood] = useState(false);
  // The date is computed from now, which is when the request will be written.
  const schedule = deletionSchedule(new Date().toISOString());

  const line = { fontSize: 13.5, color: UI.text.secondary, lineHeight: 1.75 };
  const head = { ...TYPE.headingSub, color: UI.text.primary, marginBottom: 4 };

  return createPortal(
    <div
      onClick={busy ? undefined : onCancel}
      style={modalScrim(1000)}>
      <div
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
        style={{ ...modalPanel({ width: 560 }), gap: 14 }}>
        <div style={TYPE.h2}>
          אישור מחיקת חשבון
        </div>

        <div>
          <div style={head}>מה יימחק</div>
          <div style={line}>{WHAT_IS_DELETED.join(" · ")}.</div>
        </div>

        <div>
          <div style={head}>מתי</div>
          <div style={line}>
            החשבון יושבת מיד עם האישור, והכניסה לאפליקציה תיחסם. המחיקה הסופית תתבצע ב-
            <LTR style={{ fontFamily: FONT.latin, fontWeight: 700, color: UI.text.primary }}>{formatDate(schedule?.deletionAt)}</LTR>
            {" "}· בעוד <LTR>{GRACE_DAYS}</LTR> יום.
          </div>
        </div>

        <div>
          <div style={head}>שחזור</div>
          <div style={line}>
            עד לתאריך הזה, כניסה עם אותם פרטים תציג מסך שחזור. אחריו הנתונים אינם ניתנים לשחזור.
          </div>
        </div>

        <div>
          <div style={head}>לפני שממשיכים</div>
          <div style={line}>
            ייצוא CSV שבכרטיס "ייצוא נתונים" כולל את ההחזקות בלבד · סימול, שם, כמות, מחיר קנייה,
            מחיר נוכחי, רווח/הפסד, משקל, P/E ותשואת דיבידנד. הוא אינו כולל את התראות המחיר,
            את חשבון הסימולציה ואת תשובות השאלון.
          </div>
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 9, cursor: busy ? "default" : "pointer", fontSize: 14, color: UI.text.primary, paddingTop: 2 }}>
          <input
            type="checkbox"
            checked={understood}
            disabled={busy}
            onChange={(e) => setUnderstood(e.target.checked)}
            style={{ width: 17, height: 17, accentColor: UI.caution, cursor: busy ? "default" : "pointer" }}
          />
          קראתי והבנתי
        </label>

        {error && <div style={{ fontSize: 13, color: UI.text.secondary }}>{error}</div>}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={onCancel}
            disabled={busy}
            style={{
              flex: "1 1 140px", minHeight: 46, borderRadius: T.radius.md, cursor: busy ? "default" : "pointer",
              background: "transparent", border: `1px solid ${UI.line.hairline}`,
              color: UI.text.secondary, fontSize: 15, fontWeight: 700, fontFamily: "inherit",
            }}>
            ביטול
          </button>
          <button
            onClick={onConfirm}
            disabled={!understood || busy}
            style={{
              flex: "1 1 180px", minHeight: 46, borderRadius: T.radius.md,
              cursor: (!understood || busy) ? "default" : "pointer",
              background: understood && !busy ? UI.cautionSoft : "transparent",
              border: `1px solid ${understood && !busy ? UI.cautionLine : UI.line.hairline}`,
              color: understood && !busy ? UI.caution : UI.text.muted,
              fontSize: 15, fontWeight: 800, fontFamily: "inherit",
            }}>
            {busy ? "שולח…" : "מחק את החשבון"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function DeleteAccountCard({ isGuest, userId }) {
  const [open,  setOpen]  = useState(false);
  const [busy,  setBusy]  = useState(false);
  const [error, setError] = useState(null);

  const confirm = async () => {
    setBusy(true); setError(null);
    try {
      const res = await requestAccountDeletion(userId);
      if (!res.ok) {
        setError("הבקשה לא הושלמה. נסה שוב.");
        setBusy(false);
        return;
      }
      // The server already revoked the refresh chain; this drops the local
      // session so the app returns to the login screen straight away.
      await supabase.auth.signOut();
    } catch {
      setError("הבקשה לא הושלמה. נסה שוב.");
      setBusy(false);
    }
  };

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 18, color: UI.text.primary, marginBottom: 10 }}>
        <Icon name="trash" size={18} color={UI.text.secondary}/>מחיקת חשבון
      </div>

      {isGuest ? (
        <div style={{ background: UI.fill.subtle, border: `1px solid ${UI.line.hairline}`, borderRadius: 6, padding: "12px 14px", fontSize: 13, color: UI.text.secondary, lineHeight: 1.7 }}>
          מצב אורח · מחיקת חשבון אינה זמינה. נתוני האורח נמחקים עם סיום הסשן.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 14, color: UI.text.muted, marginBottom: 14, lineHeight: 1.7 }}>
            החשבון יושבת מיד עם האישור. הנתונים יישמרו <LTR>{GRACE_DAYS}</LTR> יום וניתן לשחזר אותם
            בכל רגע במהלכם. בתום <LTR>{GRACE_DAYS}</LTR> הימים הם יימחקו סופית.
          </div>
          <button
            onClick={() => { setError(null); setOpen(true); }}
            style={{
              display: "flex", alignItems: "center", gap: 10, width: "100%",
              padding: "12px 14px", borderRadius: T.radius.md, minHeight: 46,
              background: "transparent", border: `1px solid ${UI.cautionLine}`,
              color: UI.caution, fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
            }}>
            <Icon name="trash" size={17} color={UI.caution}/>
            <span>מחיקת חשבון</span>
          </button>
        </>
      )}

      {open && (
        <ConfirmModal
          busy={busy}
          error={error}
          onCancel={() => { if (!busy) { setOpen(false); setError(null); } }}
          onConfirm={confirm}
        />
      )}
    </Card>
  );
}
